import React, { useEffect, useState } from 'react';
import { DateConverter } from '../DateConverter';
import DonutChart from '../chart/DonutChart';
import BarChart from '../chart/BarChart';
import LineChart from '../chart/LineChart';
import usersApi from '../../api/usersApi';
import { toast } from 'react-toastify';
import Loading from '../Loading';
import { Button } from '../Button';

const ReportCard = ({ token, reportType, label, fetchFunction, defaultFilters }) => {
    const [data, setData] = useState(null);
    const [users, setUsers] = useState([]);
    const [chartData, setChartData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    const [filters, setFilters] = useState({
        startDate: defaultFilters.startDate || DateConverter(new Date(), 'yyyy-MM-dd'),
        endDate: defaultFilters.endDate || DateConverter(new Date(), 'yyyy-MM-dd'),
        status: defaultFilters.status || 'Contacted',
        userId: defaultFilters.userId || '',
    });

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFilters((prev) => ({ ...prev, [name]: value }));
    };


    useEffect(() => {
        usersApi.getAllUsers(token)
            .then(data => {
                const usersList = data.data || [];
                setUsers(usersList);

                if (usersList.length > 0) {
                    setFilters((prev) => ({
                        ...prev,
                        userId: usersList[0].id
                    }));
                }
            })
            .catch(error => {
                console.log("error=====>",error)
                // toast.error("Failed to fetch users");
            });
    }, []);

    const fetchData = async () => {
        setLoading(true);
        setError('');
        setChartData(null);

        try {
            const result = await fetchFunction(token, filters);
            const resultData = result?.data ?? result;

            setData(resultData);

            if (resultData) {
                switch (reportType) {
                    case 'status-summary':
                        const statusData = resultData.leadCountByStatus || resultData;
                        setChartData({
                            type: 'donut',
                            title: 'Leads Status Distribution',
                            labels: statusData.map(item => item[0] ?? 'Unknown'),
                            series: statusData.map(item => item[1] ?? 0),
                            colors: ['#3B82F6', '#10B981', '#F59E0B', '#EF4444'],
                        });
                        break;

                    case 'created-by':
                        const createdData = resultData.leadCountByStatus || resultData;
                        setChartData({
                            type: 'donut',
                            title: 'Leads Status Distribution',
                            labels: createdData.map(item => item[0] ?? 'Unknown'),
                            series: createdData.map(item => item[1] ?? 0),
                            colors: ['#3B82F6', '#10B981', '#F59E0B', '#EF4444'],
                        });
                        break;

                    case 'created-by-range':
                        setChartData({
                            type: 'line',
                            title: 'Leads Created Over Time',
                            categories: resultData.map(item => item[0]),
                            series: [{ name: 'Leads', data: resultData.map(item => item[1]) }],
                            colors: ['#8B5CF6'],
                        });
                        break;

                    case 'leads-by-user':
                        const userData = resultData.leadCountByStatus || resultData;
                        if (Array.isArray(userData)) {
                            setChartData({
                                type: 'bar',
                                title: 'Leads By Status',
                                categories: userData.map(item => item[0] || 'Unknown'),
                                series: [{ data: userData.map(item => item[1] ?? 0) }],
                                horizontal: true,
                                colors: ['#3B82F6'],
                            });
                        }
                        break;

                    default:
                        break;
                }
            }

        } catch (err) {
            console.error(err);
            setError('Failed to load data');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, [filters]);

    const renderChart = () => {
        if (!chartData) return null;

        switch (chartData.type) {
            case 'donut': return <DonutChart {...chartData} />;
            case 'bar': return <BarChart {...chartData} />;
            case 'line': return <LineChart {...chartData} />;
            default: return null;
        }
    };

    const renderTable = () => {
        if (!data) return <p className="text-gray-500">No data available</p>;

        if (data.leadCountByStatus) {
            const tableData = data.leadCountByStatus;
            return (
                <div className="overflow-x-auto mt-3">
                    <table className="min-w-full table-auto border border-gray-200 rounded">
                        <thead className="bg-gray-100">
                            <tr>
                                <th className="px-4 py-2 border">Status</th>
                                <th className="px-4 py-2 border">Count</th>
                            </tr>
                        </thead>
                        <tbody>
                            {tableData.map((row, i) => (
                                <tr key={i} className="hover:bg-gray-50">
                                    <td className="px-4 py-2 border">{row[0] || 'Unknown'}</td>
                                    <td className="px-4 py-2 border">{row[1] ?? 0}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            );
        }

        if (Array.isArray(data)) {
            if (data.length === 0) return <p className="text-gray-500">No data available</p>;

            const headers = Object.keys(data[0] || {});
            const displayHeaders = headers.map((h) => {
                if (h === '0') return 'title';
                if (h === '1') return 'count';
                return h;
            });

            return (
                <div className="overflow-x-auto mt-3">
                    <table className="min-w-full table-auto border border-gray-200 rounded">
                        <thead className="bg-gray-100">
                            <tr>
                                {displayHeaders.map((header, i) => (
                                    <th key={i} className="px-4 py-2 border">{header}</th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {data.map((row, i) => (
                                <tr key={i} className="hover:bg-gray-50">
                                    {headers.map((key, j) => (
                                        <td key={j} className="px-4 py-2 border">
                                            {typeof row[key] === 'object' ? JSON.stringify(row[key]) : row[key] ?? 'N/A'}
                                        </td>
                                    ))}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            );
        }

        if (typeof data === 'number') {
            return (
                <div className="text-2xl font-bold text-blue-600 mt-3">
                    Total: {data}
                </div>
            );
        }

        if (typeof data === 'object' && data !== null) {
            const entries = Object.entries(data);
            return (
                <div className="mt-3 space-y-1 text-sm text-gray-800">
                    {entries.map(([key, value]) => (
                        <div key={key}>
                            <span className="font-medium">{key}:</span>{' '}
                            {typeof value === 'object' ? JSON.stringify(value) : value}
                        </div>
                    ))}
                </div>
            );
        }

        return <p className="text-gray-500">No data to display</p>;
    };

    return (
        <div className="bg-white rounded-xl border border-gray-200 shadow p-5 space-y-4">
            <div className="flex justify-between items-center">
                <h2 className="text-xl font-semibold text-gray-700">{label}</h2>
                {/* <button
                    onClick={fetchData}
                    className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
                >
                    Refresh
                </button> */}

                <Button label="Refresh" onClick={fetchData} />

            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                {reportType !== 'created-by' && reportType !== 'status-summary' && (
                    <>
                        <input
                            type="date"
                            name="startDate"
                            value={filters.startDate}
                            onChange={handleInputChange}
                            className="border p-2 rounded w-full"
                        />
                        <input
                            type="date"
                            name="endDate"
                            value={filters.endDate}
                            onChange={handleInputChange}
                            className="border p-2 rounded w-full"
                        />
                    </>
                )}

                {reportType === 'status-changed-to' && (
                    <input
                        type="text"
                        name="status"
                        value={filters.status}
                        onChange={handleInputChange}
                        placeholder="Status"
                        className="border p-2 rounded w-full"
                    />
                )}

                {reportType === 'leads-by-user' && (
                    <select
                        name="userId"
                        value={filters.userId}
                        onChange={handleInputChange}
                        className="border p-2 rounded w-full"
                    >
                        {users.map(user => (
                            <option key={user.id} value={user.id}>
                                {user.fullName}
                            </option>
                        ))}
                    </select>
                )}



            </div>


            {loading && <Loading />}
            {error && <p className="text-red-500">{error}</p>}
            {renderChart()}
            {renderTable()}
        </div>
    );
};

export default ReportCard;